// The subscription-confirm branch of handleSubmit, extracted VERBATIM (the
// `if (isSubscriptionPayment)` branch, inside `if (clientSecret && stripe &&
// elements)`). Not wrapped in its own try/catch — exceptions propagate to
// the caller's try/catch exactly as they did inline.
export const submitSubscriptionConfirm = async ({
  stripe,
  elements,
  clientSecret,
  preservedMonthlyAmount,
  monthlyAmount,
  preservedNumberOfPayments,
  numberOfPayments,
  preservedIsForever,
  isForever,
  subscriptionData,
  preservedSubscriptionDataFromParent,
  firstName,
  lastName,
  email,
  navigate,
  setError,
  setLoading,
}) => {
  // For subscriptions, ALWAYS use preserved values - they should be set when subscription was created
  // CRITICAL: Use preserved values from parent (they survive remounts)
  const monthlyAmt = preservedMonthlyAmount || parseFloat(monthlyAmount);
  const numPayments = preservedNumberOfPayments || numberOfPayments;
  const forever = preservedIsForever || isForever;

  // Validate monthly donation values before confirming payment
  if (!monthlyAmt || monthlyAmt <= 0 || isNaN(monthlyAmt)) {
    setError('Please enter a valid monthly donation amount.');
    setLoading(false);
    return;
  }

  if (!forever && (!numPayments || numPayments === 'forever' || (typeof numPayments === 'string' && numPayments.trim() === '') || (typeof numPayments === 'number' ? numPayments < 1 : parseInt(numPayments) < 1))) {
    setError('Please enter a valid number of monthly payments or select "Unlimited".');
    setLoading(false);
    return;
  }

  // For subscriptions, confirm the payment intent attached to the subscription invoice
  const { error: confirmError } = await stripe.confirmPayment({
    elements,
    clientSecret: clientSecret,
    confirmParams: {
      return_url: `${window.location.origin}/portal/donation/success`,
    },
    redirect: 'if_required',
  });

  if (confirmError) {
    setError(confirmError.message || 'Payment failed. Please try again.');
    setLoading(false);
    return;
  }

  // Retrieve payment intent with expanded charges to get receipt URL
  const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret, {
    expand: ['charges']
  });

  // Get payment intent ID from the retrieved payment intent (more reliable than parsing client secret)
  const paymentIntentId = paymentIntent?.id;

  if (!paymentIntentId) {
    setError('Could not retrieve payment intent ID. Please contact support.');
    setLoading(false);
    return;
  }

  // Get receipt URL from payment intent
  const receiptUrl = paymentIntent?.charges?.data?.[0]?.receipt_url || null;

  // Prepare donation data based on subscription type
  // Use preserved values (already validated above)
  // CRITICAL: Use preserved subscription data which survives remounts
  const activeSubscriptionData = subscriptionData || preservedSubscriptionDataFromParent;

  const donationData = {
    donationType: 'monthly',
    monthlyAmount: monthlyAmt,
    isForever: forever,
    user: {
      firstName: firstName,
      lastName: lastName,
      email: email,
      first_name: firstName,
      last_name: lastName
    },
    receiptUrl: receiptUrl,
    paymentIntentId: paymentIntentId
  };

  // Use preserved subscription data to get subscription IDs
  if (forever && activeSubscriptionData?.subscriptionId) {
    donationData.numberOfPayments = 'forever';
    donationData.totalAmount = null;
    donationData.subscriptionId = activeSubscriptionData.subscriptionId;
  } else if (activeSubscriptionData?.subscriptionScheduleId) {
    const paymentsNum = typeof numPayments === 'number' ? numPayments : parseInt(numPayments);
    donationData.numberOfPayments = paymentsNum;
    donationData.totalAmount = monthlyAmt * paymentsNum;
    donationData.subscriptionScheduleId = activeSubscriptionData.subscriptionScheduleId;
    donationData.subscriptionId = activeSubscriptionData.subscriptionId;
  } else if (activeSubscriptionData?.subscriptionId) {
    // Fallback: if we have subscriptionId but no schedule, it's a forever subscription
    const paymentsNum = typeof numPayments === 'number' ? numPayments : parseInt(numPayments);
    donationData.numberOfPayments = forever ? 'forever' : paymentsNum;
    donationData.totalAmount = forever ? null : (monthlyAmt * paymentsNum);
    donationData.subscriptionId = activeSubscriptionData.subscriptionId;
  }

  // Navigate to confirmation page (no /portal prefix needed - basename is set in Router)
  navigate('/donation/success', {
    state: { donationData }
  });
};
